import { BalanceData, GEAR_STAT_IDS, GearStat, GearTarget, Rarity } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Pcg32 } from "../maths/pcg32";
import { LegacyResources, goldFromLegacy, isLegacyResources } from "./gold";
import { ownedTowers } from "../sim/ownership";
import { affinityOf, affixOn, exchange, primaryStatOn } from "./gearStat";

export { affinityOf, affixOn, primaryStatOn, statOn } from "./gearStat";

/** Where an item is equipped: a tower archetype index, or the base. */
export interface GearOwner { readonly target: GearTarget; readonly towerIndex: number }

export interface GearAffix { readonly stat: GearStat; readonly value: Fix64 }

/**
 * One gear item (DESIGN §9.4, gear v2 docs/GEAR_V2.md). Its power axis is its item level: stats scale with it, it is
 * raised with Forge Essence up to the level of the tower or fortress wearing it, and fusion turns three of a rarity into
 * one of the next. Mutable fields: level, raised, essenceSpent, bound, locked, equippedTo.
 */
export interface GearItem {
  readonly id: number;
  readonly slot: number;
  readonly rarity: Rarity;
  readonly primaryStat: GearStat;
  /** Primary value at the flat item levels (fraction, e.g. 0.06 = +6 %): base × rarity mult. */
  readonly primaryBase: Fix64;
  readonly affixes: readonly GearAffix[];
  /** Index into `gear.mythicEffects`, −1 for none. */
  readonly mythicEffect: number;
  /** Item level, 1…`itemLevel.maxLevel`: scales the primary and the gear score, and must not pass the wearer's level. */
  level: number;
  /** Levels raised with essence (the bind rule reads it; a migrated +N counts as raised). */
  raised: number;
  /** Forge Essence spent raising it (salvage and a sale give back `essence.refund` of it; fusion fodder all of it). */
  essenceSpent: number;
  bound: boolean;
  locked: boolean;
  equippedTo: GearOwner | null;
}

export const sameOwner = (a: GearOwner, b: GearOwner): boolean => a.target === b.target && (a.target === GearTarget.Base || a.towerIndex === b.towerIndex);

/** The item-level factor stats and the gear score scale by: 1 + growthPerLevel × (level − 1), so every level adds. */
export function levelFactor(balance: BalanceData, level: number): Fix64 {
  return Fix64.One.add(balance.gear.itemLevel.growthPerLevel.mulInt(Math.max(0, level - 1)));
}

/** Primary stat at the item's level: base × levelFactor(level). */
export function primaryValue(balance: BalanceData, item: GearItem): Fix64 {
  return primaryAt(balance, item, item.level);
}

/** Primary stat the item would have at `level` (the forge preview's next-level stats). */
export function primaryAt(balance: BalanceData, item: Pick<GearItem, "primaryBase">, level: number): Fix64 {
  return item.primaryBase.mul(levelFactor(balance, level));
}

/**
 * Rolls one item from a chest (§9.3/§9.4): slot uniform over all slots, item level within `itemLevel.dropSpread`
 * below the chest level (never above it), primary from the slot's stat scaled by rarity, distinct affixes by rarity
 * count, a Mythic unique effect for Mythic items. Deterministic from the rng (the draws are those of gear v1, so a
 * chest holds the same items it always did, now at an item level where the requirement was).
 */
export function generateGear(balance: BalanceData, id: number, chestLevel: number, rarity: Rarity, rng: Pcg32, fit: GearFit | null = null): GearItem {
  const g = balance.gear;
  if (g.slots.length === 0) throw new Error("gear tables missing");
  const slot = fit !== null ? g.slots[fit.slot] : g.slots[rng.nextInt(g.slots.length)];
  const spread = g.itemLevel.dropSpread;
  const rolled = Math.max(1, chestLevel - (spread > 0 ? rng.nextInt(spread + 1) : 0));
  const level = fit !== null ? Math.max(1, Math.min(rolled, fit.maxLevelReq)) : rolled;
  const primaryBase = g.primaryBase[slot.primary].mul(g.primaryRarityMult[rarity]);
  const affixes = rollAffixes(balance, rarity, [], rng);
  const mythicEffect = rarity === Rarity.Mythic ? rollMythicEffect(balance, slot.target, rng) : -1;
  return { id, slot: slot.index, rarity, level, primaryStat: slot.primary, primaryBase, affixes, mythicEffect, raised: 0, essenceSpent: 0, bound: false, locked: false, equippedTo: null };
}

/** Adds distinct affixes (never one already held) until the rarity's count; draws as gear v1 did for a fresh item. */
export function rollAffixes(balance: BalanceData, rarity: Rarity, held: readonly GearAffix[], rng: Pcg32): GearAffix[] {
  const g = balance.gear;
  const out = [...held];
  const pool = g.affixes.map((_, i) => i).filter((i) => !held.some((a) => a.stat === g.affixes[i].stat));
  for (let n = Math.min(g.affixCount[rarity] - held.length, pool.length); n > 0; n--) {
    const pick = rng.nextInt(pool.length);
    const def = g.affixes[pool[pick]];
    pool.splice(pick, 1);
    out.push({ stat: def.stat, value: def.value });
  }
  return out;
}

/** Mythic effects an item of this kind can carry: base slots the base effects, tower slots the tower effects. */
export function mythicEffectsFor(balance: BalanceData, target: GearTarget): number[] {
  const fits = balance.gear.mythicEffects.filter((m) => (m.towerId === "base") === (target === GearTarget.Base));
  return (fits.length > 0 ? fits : balance.gear.mythicEffects).map((m) => m.index);
}

function rollMythicEffect(balance: BalanceData, target: GearTarget, rng: Pcg32): number {
  if (balance.gear.mythicEffects.length === 0) return -1;
  const from = mythicEffectsFor(balance, target);
  return from[rng.nextInt(from.length)];
}

/** A seat-targeted item (§9.3 early gear): the slot it rolls for and the highest level requirement its seats all take. */
export interface GearFit { readonly slot: number; readonly maxLevelReq: number }

/**
 * Where an early first-clear item goes (§9.3 early gear, §3.4), so the gear of levels 1–4 lands on the seats level 5's
 * readiness target counts instead of doubling up on one: per gear slot, the seats that level plans (the base, and the
 * tower types its target lists that are owned by then), less the items already owned for that slot. The slot with
 * the most seats the player can fill NOW (the base, towers owned today) still empty wins, then the most empty seats
 * in all; ties go to the rng. The requirement is capped at the lowest level among that slot's seats, so any of them
 * takes the item — a fresh unlock at level 1 included. Null when every planned seat is already covered (roll as usual).
 */
export function seatedFit(balance: BalanceData, items: readonly GearItem[], forLevel: number, ownedNow: readonly boolean[], levelOf: (owner: GearOwner) => number, rng: Pcg32): GearFit | null {
  const target = balance.readiness.targets[forLevel - 1];
  if (target === undefined) return null;
  const roster = ownedTowers(balance, forLevel - 1);
  const base: GearOwner = { target: GearTarget.Base, towerIndex: 0 };
  let best: GearFit[] = [], bestNow = 0, bestAll = 0;
  for (const slot of balance.gear.slots) {
    const owners: GearOwner[] = slot.target === GearTarget.Base ? [base] : (target.seats[slot.index] ?? []).filter((i) => roster[i]).map((i) => ({ target: GearTarget.Tower, towerIndex: i }));
    if (owners.length === 0) continue;
    const held = items.filter((it) => it.slot === slot.index).length;
    const nowSeats = owners.filter((o) => o.target === GearTarget.Base || ownedNow[o.towerIndex]).length;
    const freeNow = Math.max(0, nowSeats - held), freeAll = Math.max(0, owners.length - held);
    if (freeAll === 0) continue;
    const fit = { slot: slot.index, maxLevelReq: Math.min(...owners.map(levelOf)) };
    if (freeNow > bestNow || (freeNow === bestNow && freeAll > bestAll)) { best = [fit]; bestNow = freeNow; bestAll = freeAll; }
    else if (freeNow === bestNow && freeAll === bestAll) best.push(fit);
  }
  return best.length === 0 ? null : best.length === 1 ? best[0] : best[rng.nextInt(best.length)];
}

/** Essence to raise an item of `rarity` from `level` to level + 1: raise[rarity] × (1 + level / raiseLevelDivisor), rounded up. */
export function raiseCostAt(balance: BalanceData, rarity: Rarity, level: number): number {
  const e = balance.gear.essence, d = Math.max(1, e.raiseLevelDivisor);
  return Math.ceil((e.raise[rarity] * (d + level)) / d);
}

/** Essence to raise an item of `rarity` from level `from` to level `to` (0 when to ≤ from). */
export function raiseCostRange(balance: BalanceData, rarity: Rarity, from: number, to: number): number {
  let sum = 0;
  for (let l = from; l < to; l++) sum += raiseCostAt(balance, rarity, l);
  return sum;
}

/** What salvaging an item pays (§9.6, gear v2): gold as before by item level, and Forge Essence. */
export interface SalvageYield { readonly gold: number; readonly essence: number }

/**
 * Salvage (§9.6): gold = salvageBase[rarity] × (1 + level / 50); essence = essence.salvage[rarity] × (1 + level /
 * salvageLevelDivisor) plus `essence.refund` of the essence spent raising it.
 */
export function salvageYield(balance: BalanceData, item: GearItem): SalvageYield {
  const g = balance.gear, e = g.essence;
  const gold = Fix64.fromInt(g.salvageBase[item.rarity]).mul(Fix64.One.add(Fix64.fromInt(item.level).divInt(50))).floorToInt();
  const d = Math.max(1, e.salvageLevelDivisor);
  const essence = Math.floor((e.salvage[item.rarity] * (d + item.level)) / d) + essenceRefund(balance, item);
  return { gold, essence };
}

/** The share of an item's raise spend salvage and a Trading Post sale give back. */
export function essenceRefund(balance: BalanceData, item: GearItem): number {
  return Fix64.fromInt(item.essenceSpent).mul(balance.gear.essence.refund).floorToInt();
}

/** Lowest auction listing price for an item (§10.2 guardrail): scales with item level exactly like salvage gold. */
export function auctionFloor(balance: BalanceData, item: GearItem): number {
  return Fix64.fromInt(balance.gear.auctionMinPrice[item.rarity]).mul(Fix64.One.add(Fix64.fromInt(item.level).divInt(50))).floorToInt();
}

/** One item's gear score (§7): rarity weight × levelFactor(level), floored. */
export function itemPower(balance: BalanceData, item: Pick<GearItem, "rarity" | "level">): number {
  return levelFactor(balance, item.level).mulInt(balance.gear.powerRarity[item.rarity] ?? 0).floorToInt();
}

/** Gear score for the power score (§7): `itemPower` over equipped items. */
export function gearScore(balance: BalanceData, items: readonly GearItem[]): number {
  let sum = 0;
  for (const it of items) if (it.equippedTo !== null) sum += itemPower(balance, it);
  return sum;
}

/** What one owner's equipped set contributes to the sim (fractions; 0 = nothing). */
export interface GearBonus {
  readonly damage: Fix64;
  readonly range: Fix64;
  readonly fireRate: Fix64;
  readonly hp: Fix64;
  readonly points: Fix64;
  /** Mythic effect indexes active on this owner. */
  readonly mythic: readonly number[];
  readonly setBonus: boolean;
  /**
   * The base's troops' health and damage as a fraction added to their stat multiplier (VIP troop strength, docs/SHOP.md
   * §8.5; meta/vipLevels.ts withVipCombat). Gear never sets it; absent or zero changes nothing and is not hashed, so
   * every run without VIP troops hashes as before.
   */
  readonly troops?: Fix64;
}

export const NO_GEAR_BONUS: GearBonus = { damage: Fix64.Zero, range: Fix64.Zero, fireRate: Fix64.Zero, hp: Fix64.Zero, points: Fix64.Zero, mythic: [], setBonus: false };

/**
 * An item's primary on this owner, in the stat it counts as there (`primaryStatOn`, at the primaryBase exchange rate),
 * × affinityMult when that is the stat the tower answers best to.
 */
export function primaryOn(balance: BalanceData, item: GearItem, owner: GearOwner): Fix64 {
  const s = primaryStatOn(balance, item, owner);
  const v = exchange(primaryValue(balance, item), item.primaryStat, s, balance.gear.primaryBase);
  return s === affinityOf(balance, owner) ? v.mul(balance.gear.affinityMult) : v;
}

/** One item's share of an owner's stats, by stat (`GearStat` index): its primary and affixes as they count there. */
export function itemContribution(balance: BalanceData, item: GearItem, owner: GearOwner): Fix64[] {
  const out = GEAR_STAT_IDS.map(() => Fix64.Zero);
  const ps = primaryStatOn(balance, item, owner);
  out[ps] = out[ps].add(primaryOn(balance, item, owner));
  for (const raw of item.affixes) { const a = affixOn(balance, raw, owner); out[a.stat] = out[a.stat].add(a.value); }
  return out;
}

/** Sums an owner's equipped items: primaries (affinity included), affixes (each in the stat it counts as there), set bonus (§9.4) and Mythic effects. */
export function bonusFor(balance: BalanceData, items: readonly GearItem[], owner: GearOwner): GearBonus {
  const g = balance.gear;
  const acc = [Fix64.Zero, Fix64.Zero, Fix64.Zero, Fix64.Zero, Fix64.Zero];
  const mythic: number[] = [];
  let pieces = 0, setPieces = 0;
  for (const it of items) {
    if (it.equippedTo === null || !sameOwner(it.equippedTo, owner)) continue;
    pieces++;
    if (it.rarity >= g.setMinRarity) setPieces++;
    itemContribution(balance, it, owner).forEach((v, s) => { acc[s] = acc[s].add(v); });
    if (it.mythicEffect >= 0) mythic.push(it.mythicEffect);
  }
  const setBonus = pieces >= g.setPieces && setPieces >= g.setPieces;
  if (setBonus) { acc[GearStat.Damage] = acc[GearStat.Damage].add(g.setDamage); acc[GearStat.Range] = acc[GearStat.Range].add(g.setRange); }
  return { damage: acc[0], range: acc[1], fireRate: acc[2], hp: acc[3], points: acc[4], mythic: mythic.sort((a, b) => a - b), setBonus };
}

export interface GearItemJson {
  id: number; slot: number; rarity: number; level: number; primaryStat: number; primaryBase: string; affixes: { stat: number; value: string }[];
  mythicEffect: number; raised: number; essenceSpent: number; bound: boolean; locked: boolean; equippedTo: { target: number; towerIndex: number } | null;
}

/**
 * A gear v1 item (save schema ≤ 6): a level requirement and +0…+10 where v2 has the item level. Read only by the save
 * migration (app saves.ts v6 → v7, `foldLegacyGear`).
 */
export interface LegacyGearItemJson {
  id: number; slot: number; rarity: number; levelReq: number; primaryStat: number; primaryBase: string; affixes: { stat: number; value: string }[];
  mythicEffect: number; plus: number; bound: boolean; locked: boolean; equippedTo: { target: number; towerIndex: number } | null;
  upgradeSpent: number | LegacyResources;
}

export function gearToJson(it: GearItem): GearItemJson {
  return { id: it.id, slot: it.slot, rarity: it.rarity, level: it.level, primaryStat: it.primaryStat, primaryBase: it.primaryBase.raw.toString(), affixes: it.affixes.map((a) => ({ stat: a.stat, value: a.value.raw.toString() })), mythicEffect: it.mythicEffect, raised: it.raised, essenceSpent: it.essenceSpent, bound: it.bound, locked: it.locked, equippedTo: it.equippedTo ? { ...it.equippedTo } : null };
}

const count = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);

/** Reads an item; a v1 item that reached here unmigrated (an old export) keeps its requirement as its level. */
export function gearFromJson(j: GearItemJson | LegacyGearItemJson): GearItem {
  const v2 = j as Partial<GearItemJson>, v1 = j as Partial<LegacyGearItemJson>;
  const level = Math.max(1, count(v2.level ?? v1.levelReq) || 1);
  return { id: j.id, slot: j.slot, rarity: j.rarity as Rarity, level, primaryStat: j.primaryStat as GearStat, primaryBase: Fix64.fromRaw(BigInt(j.primaryBase)), affixes: (j.affixes ?? []).map((a) => ({ stat: a.stat as GearStat, value: Fix64.fromRaw(BigInt(a.value)) })), mythicEffect: j.mythicEffect ?? -1, raised: count(v2.raised), essenceSpent: count(v2.essenceSpent), bound: !!j.bound, locked: !!j.locked, equippedTo: j.equippedTo ? { target: j.equippedTo.target as GearTarget, towerIndex: j.equippedTo.towerIndex } : null };
}

/** Gold of a v1 item's upgrade spend (a {wood, iron, crystal} bundle on pre-gold saves, meta/gold.ts). */
export function legacySpentGold(v: unknown): number {
  if (isLegacyResources(v)) return goldFromLegacy(v);
  return count(v);
}
